import { DialogComponent, DialogOpenEvent, DialogCloseEvent, clearDialogScrollLock } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  #isDesignMode() {
    return typeof Shopify !== 'undefined' && !!Shopify.designMode;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // Horizon 4.1.3+ product-form dispatches StandardEvents instead of CartAddEvent.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (!this.#isDesignMode() && history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
      clearDialogScrollLock();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    clearDialogScrollLock();
  }

  #handleHistoryOpen = () => {
    if (this.#isDesignMode() || !isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController?.abort();
    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    this.#historyAbortController = null;
    clearDialogScrollLock();
    if (this.#isDesignMode()) return;
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    // Unlock first — Back after ATC must never leave body position:fixed.
    clearDialogScrollLock();

    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }

    clearDialogScrollLock();
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleAfterAddRedirect() {
    const action = (this.getAttribute('data-after-add') || 'drawer').trim().toLowerCase();
    if (action === 'cart') {
      window.__ssAtcNavigateAway = true;
      window.location.assign(this.getAttribute('data-cart-url') || '/cart');
      return true;
    }
    if (action === 'checkout') {
      window.__ssAtcNavigateAway = true;
      window.location.assign(this.getAttribute('data-checkout-url') || '/checkout');
      return true;
    }
    return false;
  }

  #shouldSkipDrawerOpen() {
    if (window.__ssAtcNavigateAway) return true;
    const action = (this.getAttribute('data-after-add') || 'drawer').trim().toLowerCase();
    return action === 'cart' || action === 'checkout';
  }

  #handleCartAdd = (event) => {
    if (this.#shouldSkipDrawerOpen()) {
      this.#handleAfterAddRedirect();
      return;
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Horizon 4.1.3 cart lines update (add-to-cart) — keep auto-open working with custom drawer markup.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (event.action !== 'add' || this.refs.dialog?.open) return;
    if (this.#shouldSkipDrawerOpen()) {
      this.#handleAfterAddRedirect();
      return;
    }
    if (!this.hasAttribute('auto-open')) return;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    const openDrawer = () => {
      if (this.#shouldSkipDrawerOpen()) return;
      if (!this.refs.dialog?.open) this.showDialog();
    };

    if (sourceModal?.open) {
      sourceModal.addEventListener('close', openDrawer, { once: true });
    } else {
      openDrawer();
    }
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    if (this.#shouldSkipDrawerOpen()) {
      this.#handleAfterAddRedirect();
      return;
    }

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
